import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, Landmark, ShieldCheck, CheckCircle2, Zap, ArrowRight } from 'lucide-react';
import PageLayout from '../../components/Layout/PageLayout';
import PageHeader from '../../components/PageHeader/PageHeader';
import ModernCTA from '../../components/CTA/ModernCTA';
import CertificationsTrust from '../../components/CertificationsTrust/CertificationsTrust';

// NASA SEWP VI contract vehicle + OEM Partner logos (reused from the Partners page)
import contractHolderLogo from '../../images/certifications/ContractHolder_logo.jpg';
import ciscoLogo from '../../images/brands/cisco-logo.svg';
import dellLogo from '../../images/brands/Dell_Logo.svg.png';
import hpLogo from '../../images/brands/Hewlett-Packard-Logo.wine.png';
import samsungLogo from '../../images/brands/samsung-logo.svg';

import './ModernContractsPage.scss';

// Gold "Federally Authorized" spotlight for the Cisco OEM card
const FED_CARD_STYLE = { position: 'relative', border: '2px solid #16a34a', boxShadow: '0 0 0 4px rgba(22,163,74,0.12)' };
const FED_RIBBON_STYLE = { position: 'absolute', top: '-11px', left: '50%', transform: 'translateX(-50%)', display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#16a34a', color: '#fff', fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', padding: '3px 10px', borderRadius: '999px', whiteSpace: 'nowrap', boxShadow: '0 2px 6px rgba(22,163,74,0.35)', zIndex: 2 };

const ModernContractsPage = () => {
    const oemPartners = [
        { img: ciscoLogo, name: 'Cisco' },
        { img: dellLogo, name: 'Dell' },
        { img: hpLogo, name: 'HP' },
        { img: samsungLogo, name: 'Samsung' }
    ];

    const sewpHighlights = [
        'Authorized reseller delivering enterprise technology to federal agencies',
        'Access to leading OEM hardware, software, and accessories',
        'Streamlined federal procurement with competitive pricing'
    ];

    const benefits = [
        {
            icon: <Landmark size={28} />,
            title: 'Authorized Distribution',
            description: 'Direct access to authorized OEM and distributor channels for genuine, warrantied products.'
        },
        {
            icon: <ShieldCheck size={28} />,
            title: 'Compliance & Set-Aside Eligibility',
            description: 'MBE/DBE/SBE and HUBZone certified, with active SAM.gov registration for federal awards.'
        },
        {
            icon: <Zap size={28} />,
            title: 'Fast Fulfillment',
            description: 'Quick quoting, procurement, and delivery to keep your mission and operations on schedule.'
        }
    ];

    return (
        <PageLayout>
            <PageHeader
                title="Contracts"
                subtitle="Procure leading technology through our contract vehicles and government registrations."
                breadcrumbs={[{ label: 'Contracts' }]}
            />

            {/* 1. Contract Vehicles */}
            <section className="jk-contracts">
                <div className="jk-contracts__container">
                    <div className="jk-contracts__header">
                        <span className="jk-section-label">
                            <FileText size={16} />
                            Contract Vehicles
                        </span>
                        <h2 className="jk-contracts__title">
                            How to <span className="highlight">Buy From Us</span>
                        </h2>
                        <p className="jk-contracts__subtitle">
                            We make it simple for government and enterprise customers to procure the technology
                            they need through trusted contract vehicles.
                        </p>
                    </div>

                    <div className="jk-contracts__vehicle">
                        <div className="jk-contracts__vehicle-main" style={{ position: 'relative' }}>
                            <img
                                className="jk-contracts__vehicle-logo"
                                src={contractHolderLogo}
                                alt="NASA SEWP VI Contract Holder"
                            />
                            <h3 className="jk-contracts__vehicle-name">Government-Wide Acquisition Contract</h3>
                            <p className="jk-contracts__vehicle-desc">
                                As an authorized reseller under NASA SEWP VI, we deliver enterprise technology
                                from industry-leading OEMs to federal agencies through a government-wide
                                acquisition contract built for speed, value, and compliance.
                            </p>
                            <ul className="jk-contracts__vehicle-list">
                                {sewpHighlights.map((item, index) => (
                                    <li key={index}>
                                        <CheckCircle2 size={18} />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                            <p className="jk-contracts__vehicle-note">
                                <strong>Contract Number:</strong> 80TECH26D0472
                            </p>
                            <Link to="/nasa-sewp-vi" className="jk-contracts__vehicle-link">
                                View Contract Details
                                <ArrowRight size={18} />
                            </Link>
                        </div>

                        <div className="jk-contracts__vehicle-oems">
                            <span className="jk-contracts__vehicle-oems-label">Federal Authorized Partners</span>
                            <div className="jk-contracts__oem-grid">
                                {oemPartners.map((partner, index) => {
                                    const featured = partner.name === 'Cisco';
                                    return (
                                        <div key={index} className="jk-contracts__oem" style={featured ? FED_CARD_STYLE : undefined}>
                                            {featured && (
                                                <span style={FED_RIBBON_STYLE}><ShieldCheck size={11} /> Federally Authorized</span>
                                            )}
                                            <img src={partner.img} alt={partner.name} />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 2. Certifications & registrations — reused component */}
            <CertificationsTrust />

            {/* 3. Why buy through us */}
            <section className="jk-contracts-benefits">
                <div className="jk-contracts-benefits__container">
                    <div className="jk-contracts__header">
                        <span className="jk-section-label">
                            <ShieldCheck size={16} />
                            Why Buy Through Us
                        </span>
                        <h2 className="jk-contracts__title">
                            A Trusted <span className="highlight">Procurement Partner</span>
                        </h2>
                    </div>

                    <div className="jk-contracts-benefits__grid">
                        {benefits.map((benefit, index) => (
                            <div key={index} className="jk-contracts-benefits__card">
                                <div className="jk-contracts-benefits__icon">
                                    {benefit.icon}
                                </div>
                                <h3 className="jk-contracts-benefits__card-title">{benefit.title}</h3>
                                <p className="jk-contracts-benefits__card-desc">{benefit.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <ModernCTA />
        </PageLayout>
    );
};

export default ModernContractsPage;
